/**
 * The page variable the 3D canvas takes its background from: the scene ground, a light grey of
 * its own. The plan sheet keeps the dark view ground; only the 3D sits on this one.
 */
export const SCENE_BACKGROUND_VARIABLE: string = '--sc-color-scene-ground';

/**
 * The scene background when the page variable is empty (no token sheet, a test document).
 * It repeats the `--sc-color-scene-ground` value of the token sheet.
 */
// native-ok: WebGL takes a colour value, not a CSS variable; the token sheet has no TS export
export const SCENE_BACKGROUND_FALLBACK: string = '#e6e7ea';

/** The page variable of the 100 mm floor grid line. */
export const SCENE_GRID_VARIABLE: string = '--sc-color-scene-grid';

/** The 100 mm grid line when the page variable is empty; repeats `--sc-color-scene-grid`. */
// native-ok: WebGL takes a colour value, not a CSS variable; the token sheet has no TS export
export const SCENE_GRID_FALLBACK: string = '#4a5060';

/** The page variable of the 1000 mm floor grid line. */
export const SCENE_GRID_MAJOR_VARIABLE: string = '--sc-color-scene-grid-major';

/** The 1000 mm grid line when the page variable is empty; repeats `--sc-color-scene-grid-major`. */
// native-ok: WebGL takes a colour value, not a CSS variable; the token sheet has no TS export
export const SCENE_GRID_MAJOR_FALLBACK: string = '#2f3440';

/** The two colours of the floor grid: the 100 mm line and the 1000 mm line. */
export interface ISceneGridColors {
    minor: string;
    major: string;
}

/** Picks a scene colour from the computed value of its page variable, or the given fallback. */
export function resolveSceneColor(computedValue: string | null | undefined, fallback: string): string {
    const value: string = (computedValue ?? '').trim();
    return value === '' ? fallback : value;
}

/** Picks the scene background from the computed value of the page variable. */
export function resolveSceneBackground(computedValue: string | null | undefined): string {
    return resolveSceneColor(computedValue, SCENE_BACKGROUND_FALLBACK);
}

/**
 * Reads a page variable from the document the canvas lives in. The window comes from the
 * canvas's own document, so nothing global is touched; a document without a window (server
 * rendering) gives the fallback.
 */
export function readSceneColor(canvas: HTMLCanvasElement, variable: string, fallback: string): string {
    const doc: Document = canvas.ownerDocument;
    const view: Window | null = doc.defaultView;
    if (!view) {
        return fallback;
    }
    return resolveSceneColor(view.getComputedStyle(doc.documentElement).getPropertyValue(variable), fallback);
}

/** Reads the scene background variable from the document the canvas lives in. */
export function readSceneBackground(canvas: HTMLCanvasElement): string {
    return readSceneColor(canvas, SCENE_BACKGROUND_VARIABLE, SCENE_BACKGROUND_FALLBACK);
}

/** Reads both floor grid colours from the document the canvas lives in. */
export function readSceneGridColors(canvas: HTMLCanvasElement): ISceneGridColors {
    return {
        minor: readSceneColor(canvas, SCENE_GRID_VARIABLE, SCENE_GRID_FALLBACK),
        major: readSceneColor(canvas, SCENE_GRID_MAJOR_VARIABLE, SCENE_GRID_MAJOR_FALLBACK),
    };
}
